import * as React from "react"

import { cn } from "@/lib/utils"
import { VoiceInputButton, type VoiceInputTone } from "@/components/ui/voice-input-button"
import { appendVoiceChunk } from "@/lib/voice"

export interface VoiceTextareaConfig {
  tone?: VoiceInputTone
  label?: string
  onChange?: (value: string) => void
}

export interface TextareaProps extends React.ComponentProps<"textarea"> {
  voiceInput?: VoiceTextareaConfig | boolean
}

const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(
  ({ className, voiceInput, value, onChange, ...props }, ref) => {
    const baseClass = cn(
      "flex min-h-[60px] w-full rounded-md border border-input bg-transparent px-3 py-2 text-base shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      className
    )

    if (!voiceInput) {
      return (
        <textarea
          className={baseClass}
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
        />
      )
    }

    const config: VoiceTextareaConfig = typeof voiceInput === "object" ? voiceInput : {}
    const tone: VoiceInputTone = config.tone ?? "neutral"
    const label = config.label ?? "Dictate input"

    const handleTranscript = (chunk: string) => {
      const current = typeof value === "string" ? value : ""
      const next = appendVoiceChunk(current, chunk)
      if (config.onChange) {
        config.onChange(next)
        return
      }
      if (onChange) {
        const synthetic = {
          target: { value: next },
          currentTarget: { value: next },
        } as unknown as React.ChangeEvent<HTMLTextAreaElement>
        onChange(synthetic)
      }
    }

    return (
      <div className="relative w-full">
        <textarea
          className={cn(baseClass, "pr-9")}
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
        />
        <VoiceInputButton
          tone={tone}
          size="xs"
          onTranscript={handleTranscript}
          disabled={props.disabled}
          label={label}
          className="absolute right-1.5 top-2"
        />
      </div>
    )
  }
)
Textarea.displayName = "Textarea"

export { Textarea }
